import React, { useState, useEffect, useRef } from "react";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const ImageGrid = ({ images, onDeleteImage }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const thumbnailsRef = useRef(null);
  const activeThumbRef = useRef(null);

  const imageArray = Object.values(images || {});

  const openModal = (index) => {
    setSelectedIndex(index);
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = "auto";
  };

  const handleImageClick = (index) => {
    openModal(index);
  };

  const handleDelete = (e, index) => {
    e.stopPropagation();
    onDeleteImage(index);
  };

  const goToPrevious = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    setSelectedIndex((prevIndex) =>
      prevIndex === 0 ? imageArray.length - 1 : prevIndex - 1
    );

    setTimeout(() => setIsTransitioning(false), 300);
  };

  const goToNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    setSelectedIndex((prevIndex) =>
      prevIndex === imageArray.length - 1 ? 0 : prevIndex + 1
    );

    setTimeout(() => setIsTransitioning(false), 300);
  };

  // Scroll active thumbnail into view
  useEffect(() => {
    if (isModalOpen && activeThumbRef.current && thumbnailsRef.current) {
      const container = thumbnailsRef.current;
      const activeThumb = activeThumbRef.current;

      // Calculate scroll position to center the active thumbnail
      const scrollLeft =
        activeThumb.offsetLeft -
        container.clientWidth / 2 +
        activeThumb.clientWidth / 2;

      // Smooth scroll to the position
      container.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    }
  }, [selectedIndex, isModalOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isModalOpen) return;

      switch (e.key) {
        case "Escape":
          closeModal();
          break;
        case "ArrowLeft":
          goToPrevious();
          break;
        case "ArrowRight":
          goToNext();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "auto";
    };
  }, [isModalOpen, isTransitioning]);

  if (imageArray.length === 0) return null;

  return (
    <>
      {/* Image Grid */}
      <div className="gallery-container">
        <div className="gallery-grid">
          {imageArray.map((img, index) => (
            <div
              key={index}
              className="gallery-item"
              onClick={() => handleImageClick(index)}
            >
              <div className="image-container">
                <img src={img.url} alt={img.name} className="gallery-image" />
                <button
                  className="delete-button"
                  onClick={(e) => handleDelete(e, index)}
                  aria-label="Delete image"
                >
                  <DeleteOutlineIcon style={{ fontSize: 16 }} />
                </button>
              </div>
              <p className="image-title">{img.name}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-backdrop" onClick={closeModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={closeModal}
              aria-label="Close modal"
            >
              <CloseIcon style={{ fontSize: 20 }} />
            </button>

            <div className="modal-content">
              <button
                className="nav-button prev-button"
                onClick={goToPrevious}
                aria-label="Previous image"
              >
                <ArrowBackIosNewIcon style={{ fontSize: 22 }} />
              </button>

              <div className="modal-image-wrapper">
                <img
                  src={imageArray[selectedIndex].url}
                  alt={imageArray[selectedIndex].name}
                  className={`modal-image ${
                    isTransitioning ? "transitioning" : ""
                  }`}
                />
                <div className="image-counter">
                  {selectedIndex + 1} / {imageArray.length}
                </div>
              </div>

              <button
                className="nav-button next-button"
                onClick={goToNext}
                aria-label="Next image"
              >
                <ArrowForwardIosIcon style={{ fontSize: 22 }} />
              </button>
            </div>

            <div className="modal-footer">
              <h3 className="modal-title">{imageArray[selectedIndex].name}</h3>

              <div className="thumbnail-strip" ref={thumbnailsRef}>
                {imageArray.map((img, index) => (
                  <div
                    key={index}
                    className={`thumbnail ${
                      selectedIndex === index ? "active" : ""
                    }`}
                    onClick={() => setSelectedIndex(index)}
                    ref={selectedIndex === index ? activeThumbRef : null}
                  >
                    <img src={img.url} alt={img.name} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .gallery-container {
          width: 100%;
          padding: 24px;
          user-select: none;
        }

        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
          gap: 24px;
          width: 100%;
        }

        .gallery-item {
          position: relative;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.3s ease;
        }

        .gallery-item:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
        }

        .image-container {
          position: relative;
          border-radius: 12px;
          overflow: hidden;
          aspect-ratio: 1/1;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .gallery-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .gallery-item:hover .gallery-image {
          transform: scale(1.05);
        }

        .delete-button {
          position: absolute;
          top: 8px;
          right: 8px;
          background: rgba(255, 85, 85, 0.9);
          color: white;
          border: none;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          transform: translateY(-4px);
          transition: all 0.2s ease;
          z-index: 2;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }

        .gallery-item:hover .delete-button {
          opacity: 1;
          transform: translateY(0);
        }

        .delete-button:hover {
          background: rgb(255, 47, 47);
          transform: scale(1.1);
        }

        .image-title {
          margin-top: 12px;
          font-size: 14px;
          font-weight: 500;
          color: #333;
          text-align: center;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.3);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 40px;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .modal-container {
          background: #ffffff;
          border-radius: 24px;
          width: 100%;
          max-width: 1200px;
          height: 90vh;
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          animation: slideUp 0.3s ease;
        }

        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .modal-content {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          padding: 40px;
          background-color: transparent;
        }

        .modal-close {
          position: absolute;
          top: 24px;
          right: 24px;
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10;
          transition: all 0.2s ease;
          color: #333;
        }

        .modal-close:hover {
          background: rgba(0, 0, 0, 0.15);
          transform: scale(1.1);
        }

        .nav-button {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.9);
          border: none;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10;
          transition: all 0.25s ease;
          color: #333;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .nav-button:hover {
          background: #ffffff;
          transform: translateY(-50%) scale(1.1);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
        }

        .prev-button {
          left: 24px;
        }

        .next-button {
          right: 24px;
        }

        .modal-image-wrapper {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .modal-image {
          max-width: calc(100% - 140px);
          max-height: calc(100% - 40px);
          object-fit: contain;
          border-radius: 8px;
          transition: opacity 0.3s ease, transform 0.3s ease;
        }

        .modal-image.transitioning {
          opacity: 0.5;
          transform: scale(0.97);
        }

        .image-counter {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(255, 255, 255, 0.9);
          color: #333;
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 500;
          letter-spacing: 0.5px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .modal-footer {
          padding: 20px 40px 28px;
          background: white;
          border-top: 1px solid rgba(0, 0, 0, 0.06);
        }

        .modal-title {
          color: #333;
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 16px;
          text-align: center;
        }

        .thumbnail-strip {
          display: flex;
          gap: 12px;
          overflow-x: auto;
          padding: 6px 2px;
          scrollbar-width: thin;
          scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
          justify-content: center;
          scroll-behavior: smooth;
          padding-bottom: 6px;
        }

        .thumbnail-strip::-webkit-scrollbar {
          height: 6px;
        }

        .thumbnail-strip::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.03);
          border-radius: 10px;
        }

        .thumbnail-strip::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.2);
          border-radius: 10px;
        }

        .thumbnail {
          flex: 0 0 80px;
          height: 80px;
          border-radius: 10px;
          overflow: hidden;
          cursor: pointer;
          opacity: 0.7;
          transition: all 0.25s ease;
          border: 2px solid transparent;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
          position: relative;
        }

        .thumbnail img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .thumbnail:hover img {
          transform: scale(1.05);
        }

        .thumbnail.active {
          opacity: 1;
          border-color: #3d84ff;
          transform: scale(1.1);
          box-shadow: 0 6px 12px rgba(61, 132, 255, 0.2);
        }

        .thumbnail.active::after {
          content: "";
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 8px;
          height: 8px;
          background-color: #3d84ff;
          border-radius: 50%;
        }

        .thumbnail:hover {
          opacity: 0.9;
          transform: translateY(-3px);
        }

        .thumbnail.active:hover {
          transform: scale(1.1);
        }

        @media (max-width: 768px) {
          .modal-container {
            height: 90vh;
            border-radius: 20px;
          }

          .modal-content {
            padding: 24px;
          }

          .modal-image {
            max-width: calc(100% - 80px);
          }

          .nav-button {
            width: 36px;
            height: 36px;
          }

          .prev-button {
            left: 12px;
          }

          .next-button {
            right: 12px;
          }

          .thumbnail {
            flex: 0 0 64px;
            height: 64px;
          }

          .modal-footer {
            padding: 16px 24px 20px;
          }
        }
      `}</style>
    </>
  );
};

export default ImageGrid;
